import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth.service';
import { BillingService } from '@services/billing.service';
import { NavigationService } from '@services/navigation.service';
import { UtilsService } from '@services/utils.service';
import { getTranslation } from '@shared/translate-helper';
import { catchError, Observable, of, switchMap } from 'rxjs';
import { UserService } from '../services/user.service';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionGuard implements CanActivate {
  constructor(
    private userService: UserService,
    private translate: TranslateService,
    private utilsService: UtilsService,
    private navigationService: NavigationService,
    private authService: AuthService,
    private billingService: BillingService,
    private router: Router,
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    const userData = this.userService.getUserData();

    if (!userData) {
      // If user data is missing, call initialize() to load it.
      return this.authService.initialize().pipe(
        switchMap((initSuccess) => {
          const loadedUserData = this.userService.getUserData();
          if (!loadedUserData) {
            return of(false);
          }
          return this.checkSubscription(loadedUserData);
        }),
        catchError((error) => {
          this.navigationService.navigateToLogin();
          return of(false);
        }),
      );
    } else {
      return this.checkSubscription(userData);
    }
  }

  /**
   * Checks the company subscription data using the user's company UUID.
   */
  private checkSubscription(userData: any): Observable<boolean> {
    const companyUuid = userData.company.uuid;
    if (
      (this.userService.companySubscription()?.status === 'active' ||
        this.userService.companySubscription()?.status === 'trialing') &&
      this.userService.companySubscription()?.pause_collection === null
    ) {
      return of(true);
    }

    if (
      (this.userService.companySubscription() && this.userService.companySubscription()?.status === 'paused') ||
      this.userService.companySubscription()?.pause_collection !== null
    ) {
      // Trial Expired
      if (this.userService.companySubscription()?.status === 'paused') {
        getTranslation(this.translate, ['you_need_to_subscribe_title', 'you_need_to_subscribe_description']).subscribe(
          (t: any) => {
            this.utilsService.showAlert({
              title: t['you_need_to_subscribe_title'],
              alertDescription: t['you_need_to_subscribe_description'],
              alertType: 'error',
              icon: 'circle-x',
              showActionBtn: false,
              timed: true,
              time: 5000,
            });
          },
        );
      }
      // If user paused the subscription, redirect to pricing plans
      if (this.userService.companySubscription()?.pause_collection !== null) {
        getTranslation(this.translate, ['you_need_resume_title', 'you_need_resume_subscription_description']).subscribe(
          (t: any) => {
            this.utilsService.showAlert({
              title: t['you_need_resume_title'],
              alertDescription: t['you_need_resume_subscription_description'],
              alertType: 'error',
              icon: 'circle-x',
              showActionBtn: false,
              timed: true,
              time: 5000,
            });
          },
        );
      }
      this.navigationService.navigateToPricingPlans();
      return of(false);
    }

    return this.billingService.getCompanySubscription(companyUuid).pipe(
      switchMap((res) => {
        if (!res || res.subscription.status === 'paused') {
          getTranslation(this.translate, 'you_need_to_subscribe').subscribe((t: any) => {
            this.utilsService.showAlert({
              title: t,
              alertType: 'error',
              icon: 'circle-x',
              showActionBtn: false,
              timed: true,
              time: 5000,
            });
          });
          this.navigationService.navigateToPricingPlans();
          return of(false);
        }
        return of(true);
      }),
      catchError((error) => {
        this.navigationService.navigateToLogin();
        return of(false);
      }),
    );
  }
}
